import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'

import { SkeletonRows } from '@/components/ui/skeleton'
import { FleetStatusBanner } from '../../../components/FleetStatusBanner'
import { QuotesLockNotice } from '../../../components/QuotesLockNotice'
import { TrucksTable } from '../../../components/TrucksTable'
import { Truck, useTrucks } from '../../../models/equipment'
import { FleetEditMode, rosterEditMode, showsQuotesLock, useCurrentUser } from '../../../models/user'
import { useRosterChangePrompt } from './PolicyChanges/useRosterChangePrompt'

export function Trucks() {
  const { user } = useCurrentUser({ enabled: true })
  const orgId = user?.currentOrg.id
  const { trucks, isLoading } = useTrucks({ orgId, shouldFetchAllPages: true })
  const [selectedTrucks, setSelectedTrucks] = useState<Truck[]>([])
  const quotesLocked = showsQuotesLock(user, user?.currentOrg.currentInsuranceApplication)
  const mode = rosterEditMode(user, quotesLocked)
  const readOnly = mode === FleetEditMode.LOCKED
  // Under a policy in force, a change here waits for review in a policy change request.
  const { prompt, afterRosterChange } = useRosterChangePrompt(
    mode === FleetEditMode.REVIEWED ? orgId : undefined
  )

  useEffect(() => {
    setSelectedTrucks(trucks)
  }, [trucks])

  function renderContent() {
    if (isLoading || !orgId) {
      return <SkeletonRows />
    }

    return (
      <>
        {quotesLocked && <QuotesLockNotice section="trucks" />}
        {!quotesLocked && mode !== FleetEditMode.EDITABLE && (
          <FleetStatusBanner label="trucks" mode={mode} status={user.currentOrg.status} />
        )}
        <TrucksTable
          trucks={selectedTrucks}
          setTrucks={setSelectedTrucks}
          orgId={orgId}
          addCtaOnTop
          searchable
          readOnly={readOnly}
          onAdd={afterRosterChange}
          onEdit={afterRosterChange}
          onDelete={afterRosterChange}
          onRestore={afterRosterChange}
        />
        {prompt}
      </>
    )
  }

  return (
    <>
      <Helmet>
        <title>Trucks</title>
      </Helmet>
      <div className="flex flex-col gap-5">{renderContent()}</div>
    </>
  )
}
